import React, { useEffect, useMemo, useRef, useState } from 'react';
import { View, Text, Pressable, StyleSheet, Linking, Image } from 'react-native';
import { useColorScheme } from '../foundations/colorScheme';
import {
  BadgeCheck,
  Check,
  CheckCheck,
  Info,
  Pin,
  Play,
  Send,
} from 'lucide-react-native';
import { BubbleTail } from './BubbleTail';
import { injectBubbleTailStyles } from './bubbleTailCss';
import { createTheme } from '../design-system/theme';
import { styles } from './MessageBubble.styles';
import type { Message } from '../sync/persistence';
import { ReactionsRow, type ReactionAggregate } from './ReactionsRow';
import { useTileMaxWidth } from './tileConstraint';
import { RichTextBody } from './RichTextBody';
import { LinkPreviewCard } from './attachments/LinkPreviewCard';
import type { TextBody } from '../appEvent/types';
import { PreviewLabel } from '../appEvent/PreviewLabel';
import { extractEmbeddedLinkPreview } from './parseMessageBody';
import { decodeBubbleMentions } from './decodeBubbleBody';
import { MrkdwnText, type MrkdwnPalette } from './MrkdwnText';
import { tileMrkdwnPalette } from './tileMrkdwnPalette';
import { MessageInteractionPanel } from './MessageInteractionPanel';
import { ReadByPopover } from './ReadByPopover';
import { WebPortal } from './composer/WebPortal';
import {
  MessageActionMenu,
  type MessageMenuAction,
} from './MessageActionMenu';
import { Avatar } from '../components/Avatar';
import { ThreadPreviewCard } from './ThreadPreviewCard';

const IS_WEB =
  typeof document !== 'undefined' && typeof window !== 'undefined';

// Inject the bubble-tail `::after` stylesheet once on web (no-op native).
if (IS_WEB) {
  injectBubbleTailStyles();
}

// Figma refs:
//   - 3412:5136  1-on-1 chat bubble layout (own + other)
//   - 3412:5207  Group chat bubble layout (with sender header)
//   - 2804:26325 Message states (sending / sent / failed / read)
//   - 2938:10511 "View Thread" affordance under a bubble
//   - 3412:5095  Pinned message footer
//
// Bubble palette (dark mode = exact Figma 7038:2000; light pair via tokens).
// The own/primary blue uses `accent.brand` (#537EFF) — the design token whose
// value IS the Figma blue (NOT `accent.indigo` #5C7BF0). This matches Figma
// exactly AND keeps the chat blues consistent with the mention/reaction tints
// that already use #537EFF. Other Figma values snap to the nearest EXISTING
// token, which here happen to be exact:
//   - self body bg     accent.brand   #537EFF  (Figma exact; token IS #537EFF)
//   - other body bg    surface.elevated #222429 (was surface.tertiary #3A3A3C)
//   - reply inset      #000000@0.20 over BOTH own + other bubble (Figma 7038:2000)
//   - body text self   #FFFFFF
//   - body text other  text.primary #FFFFFF (dark) / #1C1C1E (light)
//   - meta time self   rgba(255,255,255,0.40)
//   - meta time other  text.muted #6B7280
//   - failed accent    accent.rose (Figma error is #FF5353 — no token; snapped)
//   - sender name      #B5BAC3 (group, inside bubble)
//   - pinned label     #B5BAC3
//
// `lineHeight` was the squashing culprit — the previous version
// multiplied `tokens.fontSize.body (17) * tokens.lineHeight.normal (1.4)`
// to derive 23.8 while the Figma body is 16/22. The styles file now
// hardcodes the Figma values; this file is unchanged in spirit but the
// inner JSX is restructured so the body container + meta row are
// VERTICAL siblings inside a dedicated `innerContent` wrapper that
// applies the inner 6px horizontal padding from Figma's
// `Frame 1000005067`. This removes the prior bug where the bodyText
// container was `flex-wrap` and the meta row would visually merge with
// the last line of the body.

export interface MessageBubbleProps {
  message: Message;
  onReplyPress?: ((message: Message) => void) | undefined;
  onMentionPress?: ((userId: string) => void) | undefined;
  /** Tapping the sender AVATAR opens the profile popover anchored to it. */
  onAvatarPress?: ((userId: string, anchorRef: React.RefObject<unknown>) => void) | undefined;
  // Outgoing-only retry control. The host supplies this when a
  // message's `deliveryState === 'failed'`; the bubble renders a
  // rose-tinted "Not delivered" pill + an inline "Resend" Pressable
  // that calls back with the message id so the host can re-issue the
  // send. Web wires this through `WebChatRoom.handleRetry`; desktop
  // wires it through the equivalent macOS room when it's built.
  onRetryPress?: ((messageId: string) => void) | undefined;
  // Group-chat-only. When provided, a sender header row is drawn ABOVE
  // the bubble (received bubbles only) per Figma 3412:5207.
  senderName?: string | undefined;
  // Group-chat verified badge — Figma 7038:2148 ("Fred" + iconsax-verify).
  // Renders a #2CA8D4 BadgeCheck after the in-bubble sender name.
  senderVerified?: boolean | undefined;
  senderAvatar?: { readonly initials: string; readonly imageUri?: string } | undefined;
  groupAvatarGutter?: boolean | undefined;
  // Optional: when truthy, render the "View Thread" affordance under
  // the bubble (Figma 2938:10511) via `<ThreadPreviewCard>`. The host
  // passes the click handler plus optional participants/last-replier/
  // preview to enrich the card.
  threadAffordance?:
    | {
        readonly count: number;
        readonly onPress: () => void;
        readonly participants?:
          | ReadonlyArray<{ readonly initials: string; readonly imageUri?: string | undefined }>
          | undefined;
        readonly lastReplierName?: string | undefined;
        readonly preview?: string | undefined;
      }
    | undefined;
  // Optional: render the pinned-footer (Figma 3412:5095).
  isPinned?: boolean | undefined;
  // When true, the action menu's Save entry reads "Unsave" (already saved).
  isSaved?: boolean | undefined;
  // When true, the action menu's Star entry reads "Unstar" (already starred).
  isStarred?: boolean | undefined;
  // When true, this bubble lives INSIDE a thread panel — the action
  // menu hides its "Reply in Thread" entry (a thread reply can't spawn
  // a nested thread).
  inThread?: boolean | undefined;
  // When true, this bubble is the LAST of a consecutive same-sender
  // run and gets the speech tail (Figma "Polygon 3"). Computed by the
  // host (ChatRoom) alongside the grouping decision. Defaults to true
  // so a standalone bubble always has a tail.
  isLastOfGroup?: boolean | undefined;
  /**
   * First message of a consecutive same-sender run. Drives the
   * "blended"/grouped corner radii: the corners on the sender's tail
   * side (right for self, left for other) flatten where a bubble adjoins
   * a neighbour, so a run reads as one connected stack (iMessage-style).
   * Defaults to true so a standalone bubble is fully rounded.
   */
  isFirstOfGroup?: boolean | undefined;
  /** Tap the reply-quote → jump/scroll to the referenced message. */
  onReplyQuotePress?: ((messageId: string) => void) | undefined;
  // Aggregated reactions row — Figma 2804:27483 reactions strip. The
  // host computes the aggregate from the reaction projection (an
  // `0x30 REACTION` envelope per §2.4 targeting this message id); the
  // bubble renders the pills directly below itself. Tapping a pill
  // calls `onToggleReaction(emoji)` which the host wires to the wire
  // send (`com.ping.reaction` or `com.ping.reaction_remove`).
  readonly reactions?: ReadonlyArray<ReactionAggregate> | undefined;
  readonly onToggleReaction?: ((emoji: string) => void) | undefined;
  // Optional `(edited)` indicator — surfaces when the host wires an
  // edit-projection sidecar (an `0x20 EDIT` event has applied to this
  // event_id). Distinct from the dedicated `EditMessageBubble` tile
  // which renders the full Figma 2869:5218 edit chrome; here we only
  // add the inline footer to a base bubble.
  readonly editedAt?: number | undefined;
  // "Forwarded" indicator — true when this bubble is a forwarded copy
  // (`com.ping.forward`, unwrapped by the messages adapter). Renders a small
  // secondary-color header above the body, mirroring the "(edited)" footer.
  // Both the forwarder's own echo and each recipient's copy set this.
  readonly forwarded?: boolean | undefined;
  // Rich-text body — when set, the registry-decoded `TextBody`
  // (§2.2) is rendered via `RichTextBody`, replacing the legacy
  // inline `parseMessageBody` path. Hosts opt in by parsing the
  // envelope at the boundary (`parseEnvelope` → if `kind ===
  // 'com.ping.text'`, pass the decoded body here). Plain text +
  // mention-only paths leave this `undefined` so the existing fast
  // path keeps working.
  readonly richBody?: TextBody | undefined;
  // Hover-action handlers — Figma 2804:27483. Each is optional;
  // passing `undefined` hides the corresponding chip in the panel
  // (React / Reply / Menu).
  readonly onReact?: ((message: Message) => void) | undefined;
  readonly onReplyFromHover?: ((message: Message) => void) | undefined;
  /** Edit this message from the hover panel. Only supplied for the user's own
   *  text messages; the panel Edit chip renders only when this is set AND
   *  `message.isSelf`. */
  readonly onEditFromHover?: ((message: Message) => void) | undefined;
  readonly onMenuAction?:
    | ((action: MessageMenuAction, message: Message) => void)
    | undefined;
  /**
   * Resolves a display name for a reader userId from the conversation
   * member roster. When provided and `message.readBy` is non-empty,
   * tapping the read-receipt tick opens an `AnchoredPopover` listing
   * every member who has seen the message. Supply only for group
   * conversations — in 1:1 there is at most one reader and no popover
   * is needed.
   */
  readonly resolveReaderName?: ((userId: string) => string | undefined) | undefined;
  /**
   * Resolves an avatar image URI for a reader userId. Rendered in the
   * "Read by" popover alongside the reader's name.
   */
  readonly resolveReaderAvatar?: ((userId: string) => string | undefined) | undefined;
  /**
   * Number of non-self members in the group. When provided (group only),
   * the green double-check only renders once ALL members have read the
   * message (`readBy.length >= groupNonSelfMemberCount`). In 1:1 (omit
   * this prop) a single read entry is sufficient.
   */
  readonly groupNonSelfMemberCount?: number | undefined;
}

export const MessageBubble = React.memo<MessageBubbleProps>(
  ({
    message,
    onReplyPress,
    onMentionPress,
    onAvatarPress,
    onRetryPress,
    senderName,
    senderVerified,
    senderAvatar,
    groupAvatarGutter,
    threadAffordance,
    isPinned,
    isSaved,
    isStarred,
    inThread,
    isLastOfGroup = true,
    isFirstOfGroup = true,
    onReplyQuotePress,
    reactions,
    onToggleReaction,
    editedAt,
    forwarded,
    richBody,
    onReact,
    onReplyFromHover,
    onEditFromHover,
    onMenuAction,
    resolveReaderName,
    resolveReaderAvatar,
    groupNonSelfMemberCount,
  }) => {
    const scheme = useColorScheme();
    const colorScheme = scheme === 'dark' ? 'dark' : 'light';
    const theme = createTheme(colorScheme);
    // Thread side panel: cap the bubble column to the provided width (overrides
    // the default 75%-of-parent cap) so text bubbles match the media tiles.
    const tileMax = useTileMaxWidth();
    const tileMaxStyle = tileMax !== null ? { maxWidth: tileMax } : null;

    // Hover action panel state — Figma 2804:27483. Rendered as a
    // plain DOM <div> overlay inside the bubble's bubbleHoverHost
    // (which is `position: relative` and sized to the bubble's
    // actual pixel rect). On web the overlay shows on hover; on
    // native iOS/Android the events never fire so the overlay
    // stays hidden.
    const [isPanelOpen, setIsPanelOpen] = useState(false);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [readersOpen, setReadersOpen] = useState(false);
    const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // Anchor for the avatar-tap profile popover (measured by the overlay host).
    const senderAvatarRef = useRef<View | null>(null);
    // Anchor for the read-receipt popover (group: tap tick → "Seen by" list).
    const readReceiptRef = useRef<View | null>(null);
    const avatarInteractive = onAvatarPress !== undefined || onMentionPress !== undefined;
    // Bubble DOM ref + measured rect — the hover panel is portaled to
    // <body> and positioned against this rect so it can never be painted
    // over by a later media message row (each row is its own stacking
    // context; an in-row absolute panel can't escape it).
    const bubbleWrapRef = useRef<HTMLDivElement | null>(null);
    const popoverRef = useRef<HTMLDivElement | null>(null);
    const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);
    const hasHoverActions =
      onReact !== undefined ||
      onReplyFromHover !== undefined ||
      (onEditFromHover !== undefined && message.isSelf) ||
      onToggleReaction !== undefined ||
      onMenuAction !== undefined;
    // Outside-click / Escape dismissal for the open action menu (hover-out
    // is suppressed while it's open, so without this it lingers).
    useEffect(() => {
      if (!isMenuOpen || !IS_WEB) return undefined;
      const onDown = (e: MouseEvent): void => {
        const t = e.target as Node | null;
        if (
          t !== null &&
          (popoverRef.current?.contains(t) === true ||
            bubbleWrapRef.current?.contains(t) === true)
        ) {
          return;
        }
        setIsMenuOpen(false);
        setIsPanelOpen(false);
      };
      const onKey = (e: KeyboardEvent): void => {
        if (e.key === 'Escape') {
          setIsMenuOpen(false);
          setIsPanelOpen(false);
        }
      };
      document.addEventListener('mousedown', onDown, true);
      document.addEventListener('keydown', onKey, true);
      return () => {
        document.removeEventListener('mousedown', onDown, true);
        document.removeEventListener('keydown', onKey, true);
      };
    }, [isMenuOpen]);
    const cancelClose = (): void => {
      if (closeTimerRef.current !== null) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
    };
    const scheduleClose = (): void => {
      cancelClose();
      closeTimerRef.current = setTimeout(() => {
        setIsPanelOpen(false);
        closeTimerRef.current = null;
      }, 90);
    };
    useEffect(
      () => () => {
        cancelClose();
      },
      [],
    );
    const handleHoverIn = (): void => {
      if (!hasHoverActions) return;
      cancelClose();
      if (bubbleWrapRef.current !== null) {
        setAnchorRect(bubbleWrapRef.current.getBoundingClientRect());
      }
      setIsPanelOpen(true);
    };
    const handleHoverOut = (): void => {
      if (!hasHoverActions) return;
      if (isMenuOpen) return;
      scheduleClose();
    };
    const handleQuickReaction = (emoji: string): void => {
      onToggleReaction?.(emoji);
      setIsPanelOpen(false);
    };
    const handleReactChip = (): void => {
      onReact?.(message);
      setIsPanelOpen(false);
    };
    const handleReplyChip = (): void => {
      onReplyFromHover?.(message);
      setIsPanelOpen(false);
    };
    const handleEditChip = (): void => {
      onEditFromHover?.(message);
      setIsPanelOpen(false);
    };
    const handleMoreChip = (): void => {
      setIsMenuOpen((prev) => !prev);
    };
    const handleMenuSelect = (action: MessageMenuAction): void => {
      setIsMenuOpen(false);
      setIsPanelOpen(false);
      onMenuAction?.(action, message);
    };

    const isFailed = message.deliveryState === 'failed';
    const isPending = message.deliveryState === 'pending';

    // Single source of truth for the bubble fill — shared by the
    // bubble body and the speech tail so they read as one shape.
    // Figma 7038:1584: a FAILED bubble keeps its normal fill (own =
    // blue, other = dark) — only the red "Error sending message"
    // caption below signals the failure. The bubble is NOT recoloured.
    const bubbleFill = message.isSelf
      ? theme.colors.accent.brand
      : theme.colors.surface.elevated;

    // Grouped/"blended" corners (iMessage-style): within a same-sender
    // run, the corners on the sender's TAIL side (right for self, left
    // for other) flatten where a bubble adjoins a neighbour, so the run
    // reads as one connected stack. Edge corners (top of first, bottom of
    // last) stay fully rounded. The non-tail side is always fully rounded.
    const FULL = 16;
    const TIGHT = 6;
    const tailTop = isFirstOfGroup ? FULL : TIGHT;
    const tailBottom = isLastOfGroup ? FULL : TIGHT;
    const groupedCorners = message.isSelf
      ? {
          borderTopLeftRadius: FULL,
          borderBottomLeftRadius: FULL,
          borderTopRightRadius: tailTop,
          borderBottomRightRadius: tailBottom,
        }
      : {
          borderTopRightRadius: FULL,
          borderBottomRightRadius: FULL,
          borderTopLeftRadius: tailTop,
          borderBottomLeftRadius: tailBottom,
        };

    const bubbleStyle = message.isSelf
      ? [styles.bubble, styles.bubbleSelf, { backgroundColor: bubbleFill }, groupedCorners]
      : [styles.bubble, styles.bubbleOther, { backgroundColor: bubbleFill }, groupedCorners];

    const bodyTextStyle = message.isSelf
      ? [styles.bodyText, { color: '#FFFFFF' }]
      : [styles.bodyText, { color: theme.colors.text.primary }];

    // Time color: per Figma 2804:26325 the time inside the OWN bubble
    // sits at rgba(255,255,255,0.65) to keep contrast over the indigo
    // fill; the OTHER bubble uses #6B7280 directly so it reads against
    // the dark `#222429` surface.
    const timestampTextStyle = message.isSelf
      ? [styles.timestampText, { color: 'rgba(255, 255, 255, 0.40)' }]
      : [styles.timestampText, { color: theme.colors.text.muted }];

    const formattedTime = useMemo(() => formatTimestamp(message.timestamp), [message.timestamp]);

    // Slack-mrkdwn palette for the message body. Own (indigo) bubbles use
    // white ink + translucent-white washes so links / mentions / code stay
    // legible over the fill; other (dark) bubbles use the accent + surface
    // tokens. Consumed by `<MrkdwnText>` below.
    const mrkdwnPalette = useMemo<MrkdwnPalette>(
      // Shared tile palette (self/other) — the single source of truth for
      // mention/link/code styling across bubble + compact. See
      // `tileMrkdwnPalette` + the message-tile-parity memory.
      () => tileMrkdwnPalette(message.isSelf, theme),
      [message.isSelf, theme],
    );

    // Ranged mentions decoded from the envelope so multi-word display
    // names (`@aik 11`) render as one pill. Empty for a bare-text body
    // (the renderer falls back to its single-word `@handle` regex).
    const knownMentions = useMemo(
      () => decodeBubbleMentions(message.body),
      [message.body],
    );

    // Smart link preview embedded in the text envelope (`payload.link_preview`).
    // Rendered as a card directly under the bubble so text + preview read as
    // one message. Decoded defensively — a plain-text body yields undefined.
    const embeddedLinkPreview = useMemo(
      () => extractEmbeddedLinkPreview(message.body),
      [message.body],
    );

    // Truthy when the message has been read by enough recipients to
    // show the green double-check (Figma `2804:26325` "Read" state).
    // 1:1: any read entry counts. Group: ALL non-self members must have
    // read (green ✓✓ only when everyone has seen it).
    const readByCount = message.readBy?.length ?? 0;
    const hasReadReceipt = groupNonSelfMemberCount !== undefined
      ? readByCount >= groupNonSelfMemberCount && groupNonSelfMemberCount > 0
      : readByCount > 0;

    // Whether the tick (in any state — single ✓, muted ✓✓, green ✓✓) should
    // be wrapped in a Pressable that opens the "Read by" popover. Active
    // whenever at least one reader exists and the host supplied a name resolver.
    const tickPressable = resolveReaderName !== undefined && readByCount > 0;

    // Truthy when the peer ACK'd delivery but hasn't read yet — drives
    // the single-check `Check` lucide icon (Figma `2804:26325`,
    // "Delivered" state). Mutually exclusive with `hasReadReceipt`:
    // once `readBy` is non-empty the bubble switches to the
    // double-check glyph.
    const hasDeliveredReceipt =
      message.deliveredAt !== undefined && !hasReadReceipt;

    // Color for the inline DELIVERED check (single + first stroke of
    // the double-check). Per Figma 2804:26325 this is the muted
    // timestamp color (white-75% on own bubble, text-tertiary on
    // peer bubble — peers don't see their own delivery state anyway).
    const deliveredIconColor = message.isSelf
      ? 'rgba(255, 255, 255, 0.40)'
      : theme.colors.text.muted;
    // READ glyph is the green double-check (Figma 2804:26325 "read"
    // state — the double-check renders with the success-green fill,
    // visually distinct from the muted delivered double-check).
    const READ_GREEN = '#3CCB4F';
    const readIconColor = READ_GREEN;

    // Group sender name — Figma 3412:5207 (Frame 1000004564). Rendered
    // INSIDE the first bubble of an other-sender run, above the body
    // (#B5BAC3 / text.secondary, 12/16.8 w700). Self messages skip it
    // because the bubble color already disambiguates the author.
    const senderNameNode =
      senderName !== undefined && senderName.length > 0 && !message.isSelf ? (
        <View style={styles.senderNameRow}>
          <Text
            style={[styles.senderNameInner, { color: theme.colors.text.secondary }]}
            numberOfLines={1}
          >
            {senderName}
          </Text>
          {senderVerified === true ? (
            <BadgeCheck
              size={12}
              strokeWidth={2}
              color={theme.colors.accent.verified}
            />
          ) : null}
        </View>
      ) : null;

    // Thread preview card — Figma 2938:10511. Sits below the bubble;
    // only render when the host provided the affordance payload.
    const threadRow =
      threadAffordance !== undefined ? (
        <View style={styles.threadRow}>
          <ThreadPreviewCard
            count={threadAffordance.count}
            onPress={threadAffordance.onPress}
            {...(threadAffordance.participants !== undefined
              ? { participants: threadAffordance.participants }
              : {})}
            {...(threadAffordance.lastReplierName !== undefined
              ? { lastReplierName: threadAffordance.lastReplierName }
              : {})}
            {...(threadAffordance.preview !== undefined
              ? { preview: threadAffordance.preview }
              : {})}
          />
        </View>
      ) : null;

    // Pinned indicator — Figma 7038:2036. In BUBBLE mode this is a small
    // pin glyph INLINE in the meta row, just before the timestamp (the
    // standalone "Message pinned" footer is the STACKED-mode treatment,
    // owned by StackedMessageRow). Tinted like the timestamp.
    const pinnedMetaGlyph = isPinned === true ? (
      <View style={localStyles.readReceiptIcon}>
        <Pin
          size={11}
          strokeWidth={2}
          color={
            message.isSelf ? 'rgba(255, 255, 255, 0.40)' : theme.colors.text.muted
          }
        />
      </View>
    ) : null;

    // Per-message send-error footer — Figma 3412:4643. Replaces the old
    // inline "· Failed to send" run with a dedicated left-aligned line
    // below the bubble: "Error sending message" (rose 10/14) + info icon.
    // Figma 7038:1584 — caption is EXACTLY #FF5353 (no token), 10/400,
    // right-aligned under the own bubble. The caption itself is the
    // retry affordance (no separate "Resend" button — not in Figma).
    const errorRow = message.isSelf && isFailed ? (
      <Pressable
        style={styles.errorRow}
        {...(onRetryPress !== undefined
          ? {
              onPress: () => onRetryPress(message.id),
              accessibilityRole: 'button' as const,
              accessibilityLabel: 'Not delivered. Tap to resend.',
            }
          : {})}
      >
        <Info size={10} strokeWidth={2} color="#FF5353" />
        <Text style={[styles.errorText, { color: '#FF5353' }]}>Not delivered</Text>
        <Text style={[styles.errorText, { color: '#FF5353' }]}>·</Text>
        <Text
          style={[
            styles.errorText,
            { color: '#FF5353', fontWeight: '700', textDecorationLine: 'underline' },
          ]}
        >
          Resend
        </Text>
      </Pressable>
    ) : null;

    // Bare tick icon (no Pressable wrapper) — shared between the
    // pressable and non-pressable time+tick paths below.
    const tickIcon: React.ReactNode =
      message.isSelf && !isFailed && !isPending
        ? hasReadReceipt
          ? <CheckCheck size={12} strokeWidth={2} color={readIconColor} />
          : hasDeliveredReceipt
            ? <CheckCheck size={12} strokeWidth={2} color={deliveredIconColor} />
            : <Check size={12} strokeWidth={2} color={deliveredIconColor} />
        : null;

    // Inline timestamp + tick block used in both richBody and legacy body
    // paths. When tickPressable, the time AND tick are one Pressable so
    // clicking the time also opens the ReadByPopover.
    let timeAndTickMeta: React.ReactNode;
    if (message.isSelf && isFailed) {
      // Failed: errorRow handles the footer; no inline timestamp.
      timeAndTickMeta = null;
    } else if (message.isSelf && isPending) {
      timeAndTickMeta = (
        <>
          <Text style={timestampTextStyle}>{formattedTime}</Text>
          <Text style={[styles.readReceiptText, timestampTextStyle]}>{' · Sending...'}</Text>
          <View style={localStyles.readReceiptIcon}>
            <Send size={12} strokeWidth={2} color={deliveredIconColor} />
          </View>
        </>
      );
    } else if (tickPressable && tickIcon !== null) {
      timeAndTickMeta = (
        <Pressable
          ref={readReceiptRef}
          onPress={() => setReadersOpen(true)}
          hitSlop={8}
          style={localStyles.timeTick}
          accessibilityRole="button"
          accessibilityLabel="See who read this message"
        >
          <Text style={timestampTextStyle}>{formattedTime}</Text>
          <View style={localStyles.readReceiptIcon}>{tickIcon}</View>
        </Pressable>
      );
    } else {
      timeAndTickMeta = (
        <>
          <Text style={timestampTextStyle}>{formattedTime}</Text>
          {tickIcon !== null ? (
            <>
              <Text style={styles.metaSpacer}>{' '}</Text>
              <View style={localStyles.readReceiptIcon}>{tickIcon}</View>
            </>
          ) : null}
        </>
      );
    }

    // Standalone "(edited)" line — Figma 2869:5218. Below the body,
    // text.secondary 12/17 w400, NOT inline with the timestamp.
    const editedRow = editedAt !== undefined ? (
      <Text style={[styles.editedLine, { color: theme.colors.text.secondary }]}>
        (edited)
      </Text>
    ) : null;

    // Standalone "(forwarded)" line — sits UNDER the body, next to the
    // "(edited)" line and styled identically (themed secondary, 12/17 w400).
    // Set on BOTH the forwarder's own echo and every recipient's copy, so a
    // forwarded message reads the same on both sides.
    const forwardedRow = forwarded === true ? (
      <Text style={[styles.forwardedLine, { color: theme.colors.text.secondary }]}>
        (forwarded)
      </Text>
    ) : null;

    // Wrapping the Pressable in a plain DOM `<div>` is the only way
    // to reliably wire `onMouseEnter`/`onMouseLeave` in react-native-
    // web — Pressable's hover bridge drops unknown props before they
    // reach the rendered div. The wrapper sits inside the
    // `bubbleHoverHost` View (which is `position: relative`) so the
    // popover overlay below absolutely-positions against the bubble's
    // actual pixel rect.
    const hoverWrap = (child: React.ReactNode): React.ReactNode => {
      if (!IS_WEB || !hasHoverActions) return child;
      return React.createElement(
        'div',
        {
          ref: bubbleWrapRef,
          onMouseEnter: handleHoverIn,
          onMouseLeave: handleHoverOut,
          style: { display: 'inline-block' },
        },
        child,
      );
    };

    // Speech tail (web): wrap the bubble in a raw <div> carrying the
    // `::after` tail class + the bubble color as a CSS custom property.
    // The `::after` (see bubbleTailCss) masks the exact Element tail SVG
    // and is positioned at the bubble's bottom-outer corner. Only the LAST
    // bubble of a same-sender run gets it. (Native uses <BubbleTail> via
    // SvgXml — no DOM `::after` there.)
    const tailWrap = (child: React.ReactNode): React.ReactNode => {
      if (!IS_WEB || !isLastOfGroup) return child;
      return React.createElement(
        'div',
        {
          className: `ping-bubble-tail ping-bubble-tail-${message.isSelf ? 'out' : 'in'}`,
          style: {
            position: 'relative',
            display: 'inline-block',
            ['--ping-tail-color' as string]: bubbleFill,
          } as React.CSSProperties,
        },
        child,
      );
    };

    // Floating popover overlay. Flips BELOW the bubble when there's no
    // room above (message near the top), and the action submenu flips UP
    // when the panel sits low (last message) — so neither is ever clipped
    // by a viewport edge. The overlay's own pointer events keep the panel
    // alive across the 6px gap from bubble → panel.
    const PANEL_EST_H = 48;
    const MENU_EST_H = 220;
    const viewportH = typeof window !== 'undefined' ? window.innerHeight : 0;
    const placeBelow =
      anchorRect !== null && anchorRect.top < PANEL_EST_H + 6 + 8;
    const panelTopApprox =
      anchorRect === null
        ? 0
        : placeBelow
          ? anchorRect.bottom + 6
          : anchorRect.top - PANEL_EST_H - 6;
    const openMenuUp = viewportH > 0 && panelTopApprox + MENU_EST_H > viewportH;
    const popoverOverlay =
      IS_WEB && hasHoverActions && isPanelOpen && anchorRect !== null
        ? React.createElement(
            WebPortal,
            null,
            React.createElement(
              'div',
              {
                ref: popoverRef,
                onMouseEnter: () => {
                  cancelClose();
                  setIsPanelOpen(true);
                },
                onMouseLeave: () => {
                  if (!isMenuOpen) scheduleClose();
                },
                style: {
                  // Portaled to <body> + fixed at the bubble's measured
                  // rect so the panel escapes the message row's stacking
                  // context and is never hidden behind a later media row.
                  position: 'fixed',
                  ...(placeBelow
                    ? { top: anchorRect.bottom, transform: 'translateY(6px)' }
                    : {
                        top: anchorRect.top,
                        transform: 'translateY(-100%) translateY(-6px)',
                      }),
                  ...(message.isSelf
                    ? { right: Math.max(8, window.innerWidth - anchorRect.right) }
                    : { left: Math.max(8, anchorRect.left) }),
                  zIndex: 100000,
                },
              },
              React.createElement(
                'div',
                { style: { position: 'relative', display: 'inline-block' } },
                <MessageInteractionPanel
                  onSelectQuickReaction={handleQuickReaction}
                  {...(onReact !== undefined ? { onReact: handleReactChip } : {})}
                  {...(onReplyFromHover !== undefined
                    ? { onReply: handleReplyChip }
                    : {})}
                  {...(onEditFromHover !== undefined && message.isSelf
                    ? { onEdit: handleEditChip }
                    : {})}
                  {...(onMenuAction !== undefined
                    ? { onMore: handleMoreChip }
                    : {})}
                />,
                isMenuOpen && onMenuAction !== undefined
                  ? React.createElement(
                      'div',
                      {
                        style: {
                          position: 'absolute',
                          ...(openMenuUp
                            ? { bottom: '100%', marginBottom: 6 }
                            : { top: '100%', marginTop: 6 }),
                          ...(message.isSelf ? { right: 0 } : { left: 0 }),
                          zIndex: 1,
                        },
                      },
                      <MessageActionMenu
                        onSelect={handleMenuSelect}
                        isPinned={isPinned === true}
                        isSaved={isSaved === true}
                        isStarred={isStarred === true}
                        canCopy={message.body.length > 0}
                        hideReplyInThread={inThread === true}
                      />,
                    )
                  : null,
              ),
            ),
          )
        : null;

    return (
      <View style={message.isSelf ? styles.rowSelf : styles.rowOther}>
        {!message.isSelf && (senderAvatar !== undefined || groupAvatarGutter === true) ? (
          <View style={styles.avatarGutter}>
            {senderAvatar !== undefined ? (
              // Tapping the avatar opens the sender's profile popover (anchored
              // to this avatar), falling back to the legacy mention handler.
              <Pressable
                ref={senderAvatarRef}
                onPress={
                  onAvatarPress !== undefined
                    ? () => onAvatarPress(message.senderId, senderAvatarRef)
                    : onMentionPress !== undefined
                      ? () => onMentionPress(message.senderId)
                      : undefined
                }
                accessibilityRole={avatarInteractive ? 'button' : undefined}
                accessibilityLabel={avatarInteractive ? 'Open profile' : undefined}
              >
                <Avatar
                  initials={senderAvatar.initials}
                  {...(senderAvatar.imageUri !== undefined
                    ? { imageUri: senderAvatar.imageUri }
                    : {})}
                  size="small"
                  shape="rounded"
                />
              </Pressable>
            ) : null}
          </View>
        ) : null}
        <View style={message.isSelf ? [styles.bubbleColumn, styles.bubbleColumnSelf, tileMaxStyle] : [styles.bubbleColumn, tileMaxStyle]}>
          <View style={styles.bubbleHoverHost}>
          {hoverWrap(tailWrap(
          <Pressable
            onLongPress={() => onReplyPress?.(message)}
            style={bubbleStyle}
            android_ripple={{
              color: message.isSelf
                ? 'rgba(255, 255, 255, 0.2)'
                : theme.colors.surface.secondary,
            }}
          >
            {/*
              Inner content wrapper — Figma `Frame 1000005067` with
              paddingL=6 paddingR=6 paddingT=0 paddingB=0 and a 2px
              gap. Body + meta row sit here as vertical siblings.
            */}
            <View style={styles.innerContent}>
              {senderNameNode}
              {message.inReplyTo && (
                // Reply quote — Figma 7038:2000. Nested rounded inset
                // card (NOT a left-border strip). EXACT Figma fills:
                //   - own (blue #537EFF) bubble  → #3E6AE6 (darker blue)
                //   - other (dark #222429) bubble → #FFFFFF@0.10 (lighter)
                // Tapping jumps to the referenced message.
                <Pressable
                  onPress={
                    onReplyQuotePress !== undefined &&
                    message.inReplyTo.messageId !== undefined
                      ? () => onReplyQuotePress(String(message.inReplyTo?.messageId))
                      : undefined
                  }
                  accessibilityRole={onReplyQuotePress !== undefined ? 'button' : undefined}
                  accessibilityLabel="Go to replied message"
                  style={[
                    styles.replyCard,
                    {
                      // Self sits on the brand fill → a slightly-darker blue
                      // inset + white ink. OTHER sits on a NEUTRAL surface, so
                      // the inset + text must be THEMED — the old hardcoded
                      // white-on-white/10 was invisible in light mode.
                      backgroundColor: message.isSelf
                        ? '#3E6AE6'
                        : theme.colors.surface.secondary,
                    },
                  ]}
                >
                  <View style={styles.replyCardRow}>
                    {message.inReplyTo.thumbnailUri !== undefined &&
                    message.inReplyTo.thumbnailUri.length > 0 ? (
                      <View style={styles.replyThumbWrap}>
                        <Image
                          source={{ uri: message.inReplyTo.thumbnailUri }}
                          style={styles.replyThumb}
                          resizeMode="cover"
                          accessibilityLabel={
                            message.inReplyTo.thumbnailKind === 'video'
                              ? 'Video reply thumbnail'
                              : 'Photo reply thumbnail'
                          }
                        />
                        {message.inReplyTo.thumbnailKind === 'video' ? (
                          <View style={styles.replyThumbPlay}>
                            <Play size={12} strokeWidth={2} color="#FFFFFF" fill="#FFFFFF" />
                          </View>
                        ) : null}
                      </View>
                    ) : null}
                    <View style={styles.replyCardInner}>
                      {message.inReplyTo.senderName !== undefined &&
                      message.inReplyTo.senderName.length > 0 ? (
                        <Text
                          style={[
                            styles.replyQuoteName,
                            {
                              color: message.isSelf ? '#FFFFFF' : theme.colors.text.primary,
                            },
                          ]}
                          numberOfLines={1}
                        >
                          {message.inReplyTo.senderName}
                        </Text>
                      ) : null}
                      <PreviewLabel
                        text={message.inReplyTo.preview}
                        color={
                          message.isSelf
                            ? 'rgba(255, 255, 255, 0.70)'
                            : theme.colors.text.secondary
                        }
                        style={styles.replyText}
                        numberOfLines={2}
                      />
                    </View>
                  </View>
                </Pressable>
              )}
              {/*
                Inline body + timestamp layout. Every chunk (body text,
                mentions, time, read-receipt) is a `<Text>` sibling inside
                `bodyContainer` which is `flex-row flex-wrap items-flex-end`.
                Short messages keep body + time on the same line so the
                bubble shrink-wraps to a compact pill. Long messages wrap
                naturally; the time settles at the trailing edge of the
                last line (or wraps to a new short row if the last word
                fills the line). This mirrors iMessage / WhatsApp /
                Figma 3412:5136 placement.
              */}
              {richBody !== undefined ? (
                /*
                  Doc-spec rich text (§2.2). Renders bold / italic /
                  strike / inline-code spans, standalone code blocks,
                  and spoiler ranges. Mentions arrive as `Mention[]`
                  with explicit ranges + display names — the renderer
                  inlines them as `@Name` pills. The legacy fast path
                  below is bypassed entirely when this prop is set.
                */
                <View style={styles.bodyContainer}>
                  <RichTextBody
                    body={richBody}
                    textStyle={{
                      ...styles.bodyText,
                      color: message.isSelf ? '#FFFFFF' : theme.colors.text.primary,
                    }}
                    // Same shared tile palette as the mrkdwn path so mention
                    // pills read identically regardless of which body renderer
                    // the host feeds (fixes self/other + compact hue drift).
                    mentionColor={mrkdwnPalette.mention}
                    mentionBackgroundColor={mrkdwnPalette.mentionBackground}
                    {...(onMentionPress !== undefined ? { onMentionPress } : {})}
                  />
                  <Text style={styles.metaSpacer}>{' '}</Text>
                  {pinnedMetaGlyph}
                  {timeAndTickMeta}
                </View>
              ) : (
                /*
                  Default text body — full Slack-mrkdwn rendering (bold /
                  italic / strike / inline-code / links / @mentions plus
                  blockquotes, fenced code blocks, and nested lists). The
                  trailing meta (8px spacer + pin glyph + time/tick) flows
                  inline on a simple message's wrapped line and drops to a
                  trailing row for rich/multi-line bodies.
                */
                <MrkdwnText
                  body={message.body}
                  {...(message.mentions !== undefined
                    ? { mentions: message.mentions }
                    : {})}
                  {...(knownMentions.length > 0 ? { knownMentions } : {})}
                  textStyle={StyleSheet.flatten(bodyTextStyle)}
                  palette={mrkdwnPalette}
                  {...(onMentionPress !== undefined ? { onMentionPress } : {})}
                  onLinkPress={(url) => {
                    void Linking.openURL(url).catch(() => undefined);
                  }}
                  trailingMeta={
                    <>
                      <Text style={styles.metaSpacer}>{' '}</Text>
                      {pinnedMetaGlyph}
                      {timeAndTickMeta}
                    </>
                  }
                />
              )}
            </View>
            {/*
              Speech tail — exact Figma "Polygon 3" rounded triangle
              (node 3093:91192). Only the LAST bubble of a consecutive
              same-sender run gets it: bottom-right for own, bottom-left
              for other (outer edge). Rendered via react-native-svg with
              the bubble fill so it reads as a continuous tail.
            */}
            {/* Native (macOS) speech tail via SvgXml. On web the tail is
                a CSS `::after` on the wrapping div (see tailWrap /
                bubbleTailCss) so this is skipped there. */}
            {isLastOfGroup && !IS_WEB ? (
              <BubbleTail fill={bubbleFill} side={message.isSelf ? 'right' : 'left'} />
            ) : null}
          </Pressable>,
          ))}
          {popoverOverlay}
          </View>
          {forwardedRow}
          {editedRow}
          {errorRow}
          {threadRow}
          {embeddedLinkPreview !== undefined ? (
            <View
              className={`flex flex-row ${message.isSelf ? 'justify-end' : 'justify-start'} pt-1`}
            >
              <LinkPreviewCard
                url={embeddedLinkPreview.url}
                title={embeddedLinkPreview.title}
                description={embeddedLinkPreview.description}
                imageUri={embeddedLinkPreview.imageUri}
                isSelf={message.isSelf}
                onOpen={() => {
                  void Linking.openURL(embeddedLinkPreview.url).catch(() => undefined);
                }}
              />
            </View>
          ) : null}
          {reactions !== undefined && reactions.length > 0 ? (
            <ReactionsRow
              reactions={reactions}
              isSelf={message.isSelf}
              {...(onToggleReaction !== undefined ? { onToggleReaction } : {})}
            />
          ) : null}
          {/* Readers popover — group chats only; opens when the read tick is pressed. */}
          <ReadByPopover
            anchorRef={readReceiptRef as { current: unknown }}
            open={readersOpen}
            onClose={() => setReadersOpen(false)}
            placement="top"
            align={message.isSelf ? 'end' : 'start'}
            readers={(message.readBy ?? []).map((r) => {
              // Never surface a raw account id as a reader name — neutral
              // "Member" placeholder until the roster resolves the reader.
              const name = resolveReaderName?.(r.userId) ?? 'Member';
              const avatarUri = resolveReaderAvatar?.(r.userId);
              return {
                userId: r.userId,
                name,
                ...(avatarUri !== undefined ? { avatarUri } : {}),
                timestamp: r.timestamp,
              };
            })}
          />
          {/* Retry is wired into the `errorRow` caption above (Figma
              7038:1584 has no separate "Resend" button). */}
        </View>
      </View>
    );
  },
);

MessageBubble.displayName = 'MessageBubble';

function formatTimestamp(timestamp: number): string {
  const date = new Date(timestamp);
  return new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(date);
}

// Local-only style for inline lucide icons inside `bodyContainer`. The
// shared `MessageBubble.styles.ts` is owned by a parallel work stream
// and is off-limits here; this small block keeps the inline read-
// receipt icon aligned with the timestamp baseline without forking
// that file.
const localStyles = StyleSheet.create({
  // The icon's intrinsic bounding box is 12x12; pull it down 2px so it
  // sits on the same optical baseline as the small caption text in
  // `timestampText` (which also has `paddingBottom: 2`).
  readReceiptIcon: {
    paddingBottom: 2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  // Combined time+tick pressable — same baseline alignment as bodyContainer.
  timeTick: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: 2,
  },
});
